/**
 * Live collaboration (docs/17-live-kollaboration.md): changes to a document as
 * small operations that address values by key and list items by id, never by
 * position. The server applies them in the order they arrive; every client
 * applies the same operations in the same order, so all converge.
 *
 * Merging rule: per value, the change that reaches the server last wins. Two
 * people moving different elements, or changing different properties of the
 * same element, never get in each other's way.
 */

/** Object key, or `{ id }` for the item with that id in a list of objects. */
export type PathSegment = string | { id: string };
export type Path = PathSegment[];

export type LiveOp =
  /** Sets an object key (or replaces the list item with that id). */
  | { o: "set"; p: Path; v: unknown }
  /** Deletes an object key (or removes the list item with that id). */
  | { o: "del"; p: Path }
  /**
   * Inserts `id` into the list at `p`: at the start when `a` (the item before
   * it) is null, at the end when `b` (the item after it) is null, else after
   * `a`, or before `b` when `a` is gone. `v` is the item for lists of objects.
   * An id that is already there moves instead.
   */
  | { o: "ins"; p: Path; id: string; a: string | null; b: string | null; v?: unknown }
  /** Removes `id` from the list at `p`. */
  | { o: "rm"; p: Path; id: string }
  /** Moves `id` within the list at `p` (placement like `ins`). */
  | { o: "mv"; p: Path; id: string; a: string | null; b: string | null };

type Json = unknown;
type JsonObject = Record<string, Json>;

// ---------------------------------------------------------------------------
// Which arrays are lists with ids
// ---------------------------------------------------------------------------

type ListKind = "items" | "ids";

/**
 * Lists whose entries are addressed by id: pages, guides, clips and tracks
 * (objects with an `id`), children of pages and groups (element ids). All other arrays
 * (gradient stops, swatches, …) are single values.
 */
function listKind(path: Path): ListKind | null {
  const [first, second, third] = path;
  if (path.length === 1 && (first === "pages" || first === "guides")) return "items";
  if (
    path.length === 2 &&
    first === "timeline" &&
    (second === "clips" ||
      second === "overlays" ||
      second === "audio" ||
      second === "tracks" ||
      second === "trackGroups")
  ) {
    return "items";
  }
  if (path.length === 3 && third === "children") {
    if (first === "pages" && typeof second === "object") return "ids";
    if (first === "elements" && typeof second === "string") return "ids";
  }
  return null;
}

function isObject(value: Json): value is JsonObject {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function itemId(item: Json): string | null {
  return isObject(item) && typeof item.id === "string" ? item.id : null;
}

/** Ids of a list, or null when it cannot be treated as one (missing or duplicate ids). */
function listIds(list: Json[], kind: ListKind): string[] | null {
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const item of list) {
    const id = kind === "ids" ? (typeof item === "string" ? item : null) : itemId(item);
    if (id === null || seen.has(id)) return null;
    seen.add(id);
    ids.push(id);
  }
  return ids;
}

export function deepEqual(a: Json, b: Json): boolean {
  if (a === b) return true;
  if (typeof a !== typeof b || a === null || b === null || typeof a !== "object") return false;
  if (Array.isArray(a)) {
    if (!Array.isArray(b) || a.length !== b.length) return false;
    return a.every((item, index) => deepEqual(item, b[index]));
  }
  if (Array.isArray(b)) return false;
  const ak = Object.keys(a as JsonObject).filter((key) => (a as JsonObject)[key] !== undefined);
  const bk = Object.keys(b as JsonObject).filter((key) => (b as JsonObject)[key] !== undefined);
  if (ak.length !== bk.length) return false;
  return ak.every((key) => deepEqual((a as JsonObject)[key], (b as JsonObject)[key]));
}

// ---------------------------------------------------------------------------
// Diff: the operations that turn `a` into `b`
// ---------------------------------------------------------------------------

/**
 * Operations that turn `a` into `b`. Unchanged subtrees are skipped by
 * identity, so with structural sharing (Immer) the cost follows the size of
 * the change, not of the document.
 */
export function diff(a: Json, b: Json): LiveOp[] {
  const out: LiveOp[] = [];
  diffInto(a, b, [], out);
  return out;
}

function diffInto(a: Json, b: Json, path: Path, out: LiveOp[]) {
  if (a === b) return;
  const kind = listKind(path);
  if (kind && Array.isArray(a) && Array.isArray(b)) {
    const aIds = listIds(a, kind);
    const bIds = listIds(b, kind);
    if (aIds && bIds) {
      diffList(a, b, aIds, bIds, kind, path, out);
      return;
    }
  }
  if (isObject(a) && isObject(b)) {
    for (const key of Object.keys(a)) {
      if (a[key] !== undefined && b[key] === undefined) out.push({ o: "del", p: [...path, key] });
    }
    for (const key of Object.keys(b)) {
      const value = b[key];
      if (value === undefined) continue;
      if (a[key] === undefined) out.push({ o: "set", p: [...path, key], v: value });
      else diffInto(a[key], value, [...path, key], out);
    }
    return;
  }
  if (path.length === 0) throw new Error("Das Dokument muss ein Objekt sein");
  if (!deepEqual(a, b)) out.push({ o: "set", p: path, v: b });
}

function diffList(
  a: Json[],
  b: Json[],
  aIds: string[],
  bIds: string[],
  kind: ListKind,
  path: Path,
  out: LiveOp[],
) {
  const inB = new Set(bIds);
  const aIndex = new Map(aIds.map((id, index) => [id, index]));
  for (const id of aIds) if (!inB.has(id)) out.push({ o: "rm", p: path, id });
  // Items that keep their relative order stay; everything else moves next to
  // its new neighbour (longest increasing subsequence of the old positions).
  const common = bIds.filter((id) => aIndex.has(id));
  const stay = longestIncreasing(common.map((id) => aIndex.get(id)!));
  const staying = new Set(stay.map((index) => common[index]!));
  bIds.forEach((id, index) => {
    const after = index > 0 ? bIds[index - 1]! : null;
    const before = index < bIds.length - 1 ? bIds[index + 1]! : null;
    if (!aIndex.has(id)) {
      out.push(
        kind === "items"
          ? { o: "ins", p: path, id, a: after, b: before, v: b[index] }
          : { o: "ins", p: path, id, a: after, b: before },
      );
    } else if (!staying.has(id)) {
      out.push({ o: "mv", p: path, id, a: after, b: before });
    }
  });
  if (kind === "items") {
    bIds.forEach((id, index) => {
      const from = aIndex.get(id);
      if (from !== undefined) diffInto(a[from], b[index], [...path, { id }], out);
    });
  }
}

/** Indices (into `values`) of one longest strictly increasing subsequence. */
function longestIncreasing(values: number[]): number[] {
  const tails: number[] = [];
  const previous = new Array<number>(values.length).fill(-1);
  values.forEach((value, index) => {
    let low = 0;
    let high = tails.length;
    while (low < high) {
      const mid = (low + high) >> 1;
      if (values[tails[mid]!]! < value) low = mid + 1;
      else high = mid;
    }
    if (low > 0) previous[index] = tails[low - 1]!;
    tails[low] = index;
  });
  const result: number[] = [];
  let current = tails.length > 0 ? tails[tails.length - 1]! : -1;
  while (current !== -1) {
    result.push(current);
    current = previous[current]!;
  }
  return result.reverse();
}

// ---------------------------------------------------------------------------
// Apply
// ---------------------------------------------------------------------------

/**
 * Applies operations without mutating `doc` (unchanged parts are shared).
 * Operations whose target no longer exists (someone deleted the element) are
 * skipped. `repairDocument` then fixes references, so the result is the same
 * on the server and on every client.
 */
export function applyOps<T>(doc: T, ops: readonly LiveOp[]): T {
  let current: Json = doc;
  for (const op of ops) current = applyOp(current, op);
  return repairDocument(current) as T;
}

function applyOp(root: Json, op: LiveOp): Json {
  if (op.o === "set" || op.o === "del") {
    if (op.p.length === 0) return root;
    const parentPath = op.p.slice(0, -1);
    const last = op.p[op.p.length - 1]!;
    return updateAt(root, parentPath, 0, (parent) => {
      if (typeof last === "object") {
        if (!Array.isArray(parent)) return parent;
        const index = parent.findIndex((item) => itemId(item) === last.id);
        if (index < 0) return parent;
        const next = parent.slice();
        if (op.o === "del") next.splice(index, 1);
        else if (itemId(op.v) === last.id) next[index] = op.v;
        else return parent;
        return next;
      }
      if (!isObject(parent)) return parent;
      if (op.o === "del") {
        if (!(last in parent)) return parent;
        const { [last]: _removed, ...rest } = parent;
        return rest;
      }
      if (parent[last] === op.v) return parent;
      return { ...parent, [last]: op.v };
    });
  }
  return updateAt(root, op.p, 0, (list) => {
    if (!Array.isArray(list)) return list;
    const keyOf = (item: Json) => (typeof item === "string" ? item : itemId(item));
    const index = list.findIndex((item) => keyOf(item) === op.id);
    if (op.o === "rm") {
      if (index < 0) return list;
      const next = list.slice();
      next.splice(index, 1);
      return next;
    }
    if (op.o === "mv" && index < 0) return list;
    let item: Json;
    if (op.o === "ins") {
      if (op.v !== undefined) {
        if (itemId(op.v) !== op.id) return list;
        item = op.v;
      } else {
        item = op.id;
      }
      // Lists hold either ids or objects, never both.
      const mixed = list.some((entry) => typeof entry === "string") !== (typeof item === "string");
      if (list.length > 0 && mixed) return list;
    } else {
      item = list[index];
    }
    const next = list.slice();
    if (index >= 0) next.splice(index, 1);
    next.splice(placement(next, keyOf, op.a, op.b), 0, item);
    return next;
  });
}

function placement(
  list: Json[],
  keyOf: (item: Json) => string | null,
  after: string | null,
  before: string | null,
): number {
  // First or last stays first or last (things added at the same time end up
  // on top in the order they arrive).
  if (after === null) return 0;
  if (before === null) return list.length;
  const afterIndex = list.findIndex((item) => keyOf(item) === after);
  if (afterIndex >= 0) return afterIndex + 1;
  const beforeIndex = list.findIndex((item) => keyOf(item) === before);
  return beforeIndex >= 0 ? beforeIndex : list.length;
}

/** Copy-on-write update of the value at `path`; unresolvable paths change nothing. */
function updateAt(node: Json, path: Path, depth: number, fn: (value: Json) => Json): Json {
  if (depth === path.length) return fn(node);
  const segment = path[depth]!;
  if (typeof segment === "object") {
    if (!Array.isArray(node)) return node;
    const index = node.findIndex((item) => itemId(item) === segment.id);
    if (index < 0) return node;
    const child = node[index];
    const updated = updateAt(child, path, depth + 1, fn);
    if (updated === child) return node;
    const next = node.slice();
    next[index] = updated;
    return next;
  }
  if (!isObject(node) || !(segment in node)) return node;
  const child = node[segment];
  const updated = updateAt(child, path, depth + 1, fn);
  if (updated === child) return node;
  return { ...node, [segment]: updated };
}

// ---------------------------------------------------------------------------
// Repair: references that concurrent changes can break
// ---------------------------------------------------------------------------

/**
 * Fixes what two concurrent, individually valid changes can break:
 * references to deleted elements, an element that ended up in two places
 * (moved to another page by one person and grouped by another), groups
 * containing each other or nothing, and clip ids used twice.
 *
 * Deterministic and minimal: unchanged parts keep their identity.
 */
export function repairDocument(doc: Json): Json {
  if (!isObject(doc) || !Array.isArray(doc.pages) || !isObject(doc.elements)) return doc;
  const elements = doc.elements;
  const claimed = new Set<string>();
  /** New children lists (by owner), only where something changed. */
  const pageChildren = new Map<number, string[]>();
  const groupChildren = new Map<string, string[]>();
  const removed = new Set<string>();

  const isGroup = (id: string) => {
    const element = elements[id];
    return isObject(element) && element.type === "group" && Array.isArray(element.children);
  };

  /** Claims the children of an owner in order; returns the list to keep. */
  const visit = (children: Json[]): string[] => {
    const kept: string[] = [];
    for (const child of children) {
      if (typeof child !== "string" || !isObject(elements[child]) || claimed.has(child)) continue;
      claimed.add(child);
      if (isGroup(child) && !visitGroup(child)) {
        removed.add(child);
        continue;
      }
      kept.push(child);
    }
    return kept;
  };

  /** False when the group has no children left (it is then removed). */
  const visitGroup = (id: string): boolean => {
    const group = elements[id] as JsonObject;
    const children = group.children as Json[];
    const kept = visit(children);
    if (kept.length !== children.length || kept.some((child, index) => child !== children[index])) {
      groupChildren.set(id, kept);
    }
    return kept.length > 0;
  };

  doc.pages.forEach((page, index) => {
    if (!isObject(page) || !Array.isArray(page.children)) return;
    const children = page.children;
    const kept = visit(children);
    if (kept.length !== children.length) pageChildren.set(index, kept);
  });
  // Groups that are not on any page (left over after a concurrent delete) are
  // roots of their own: this also breaks cycles between them.
  for (const id of Object.keys(elements).sort()) {
    if (claimed.has(id) || !isGroup(id)) continue;
    claimed.add(id);
    if (!visitGroup(id)) removed.add(id);
  }

  let result: JsonObject = doc;
  if (pageChildren.size > 0) {
    result = {
      ...result,
      pages: doc.pages.map((page, index) =>
        pageChildren.has(index)
          ? { ...(page as JsonObject), children: pageChildren.get(index) }
          : page,
      ),
    };
  }
  if (groupChildren.size > 0 || removed.size > 0) {
    const nextElements: JsonObject = { ...elements };
    for (const [id, children] of groupChildren) {
      nextElements[id] = { ...(elements[id] as JsonObject), children };
    }
    for (const id of removed) delete nextElements[id];
    result = { ...result, elements: nextElements };
  }
  const timeline = repairTimeline(doc.timeline);
  if (timeline !== doc.timeline) result = { ...result, timeline };
  return result;
}

/** Clip ids are unique across all tracks: later duplicates go. */
function repairTimeline(timeline: Json): Json {
  if (!isObject(timeline)) return timeline;
  const seen = new Set<string>();
  let result = timeline;
  for (const track of ["clips", "overlays", "audio"] as const) {
    const clips = timeline[track];
    if (!Array.isArray(clips)) continue;
    const kept = clips.filter((clip) => {
      const id = itemId(clip);
      if (id === null) return true;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
    if (kept.length !== clips.length) result = { ...result, [track]: kept };
  }
  return result;
}

// ---------------------------------------------------------------------------
// Helpers for undo history
// ---------------------------------------------------------------------------

function pathKey(path: Path): string {
  return JSON.stringify(path);
}

function overlaps(a: Path, b: Path): boolean {
  const length = Math.min(a.length, b.length);
  for (let index = 0; index < length; index++) {
    const x = a[index]!;
    const y = b[index]!;
    if (typeof x === "string" ? x !== y : typeof y === "string" || x.id !== y.id) return false;
  }
  return true;
}

/**
 * Appends a change to a history entry. Repeated sets of the same values
 * (dragging, typing) replace the earlier value instead of growing the entry:
 * the entry's undo still restores the value from before the first change.
 */
export function appendChange(
  entry: { redo: LiveOp[]; undo: LiveOp[] },
  redo: LiveOp[],
  undo: LiveOp[],
): { redo: LiveOp[]; undo: LiveOp[] } {
  const simple =
    redo.length > 0 &&
    redo.every((op) => op.o === "set") &&
    redo.every((op) => {
      for (let index = entry.redo.length - 1; index >= 0; index--) {
        const earlier = entry.redo[index]!;
        if (!overlaps(earlier.p, op.p)) continue;
        return earlier.o === "set" && pathKey(earlier.p) === pathKey(op.p);
      }
      return false;
    });
  if (simple) {
    const byPath = new Map(redo.map((op) => [pathKey(op.p), op]));
    return {
      redo: entry.redo.map((op) => byPath.get(pathKey(op.p)) ?? op),
      undo: entry.undo,
    };
  }
  return { redo: [...entry.redo, ...redo], undo: [...undo, ...entry.undo] };
}
